
import React, { useState } from "react";

import {
  FaArrowRight,
  FaCar,
  FaChevronDown,
  FaChevronUp,
  FaMapMarkerAlt,
  FaRoute,
  FaUsers,
} from "react-icons/fa";

/* ==========================================================================
   HELPERS
   ========================================================================== */

const number = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const money = (value) =>
  number(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const km = (value) =>
  number(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });

const rate = (value) =>
  number(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

/* ==========================================================================
   ETAPE AVEC MOUVEMENTS DE PASSAGERS
   ========================================================================== */

function StopMovements({ stop, index }) {
  if (!stop) return null;

  const boarding = number(stop.boarding);
  const alighting = number(stop.alighting);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-start gap-2">
        <FaMapMarkerAlt className="mt-1 shrink-0 text-indigo-500" />

        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-500">
            Arrêt {index + 1}
          </div>

          <div className="mt-1 break-words text-sm font-semibold text-slate-800">
            {stop.address || "Adresse non renseignée"}
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {boarding > 0 && (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                +{boarding} montée(s)
              </span>
            )}

            {alighting > 0 && (
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                -{alighting} descente(s)
              </span>
            )}

            {boarding === 0 && alighting === 0 && (
              <span className="text-xs text-slate-500">
                Arrêt intermédiaire
              </span>
            )}
          </div>

          {stop.names && (
            <div className="mt-2 text-xs leading-5 text-slate-600">
              Personnes déclarées :{" "}
              <strong>{stop.names}</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   PARCOURS ALLER OU RETOUR
   ========================================================================== */

function JourneyDetails({
  title,
  journey,
  declaredJourney,
  variant = "blue",
}) {
  if (!journey) return null;

  const legs = Array.isArray(journey.legs)
    ? journey.legs
    : [];

  const stops = Array.isArray(declaredJourney?.stops)
    ? declaredJourney.stops
    : [];

  const isReturn = variant === "violet";

  const headerClass = isReturn
    ? "bg-violet-50 text-violet-800"
    : "bg-blue-50 text-blue-800";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${headerClass}`}
      >
        <div className="flex items-center gap-2 text-sm font-bold">
          <FaCar />
          {title}
        </div>

        <div className="text-right text-sm font-bold">
          {km(journey.distance_km)} km
          <span className="mx-2 opacity-50">•</span>
          {money(journey.montant_trajet)} €
        </div>
      </div>

      {/* TRONCONS */}

      <div className="divide-y divide-slate-100">
        {legs.map((leg, index) => (
          <div
            key={`${index}-${leg.origin}`}
            className="p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                    {index + 1}
                  </span>

                  <div className="min-w-0 space-y-1">
                    <div className="break-words text-sm font-semibold text-slate-800">
                      {leg.origin}
                    </div>

                    <div className="flex items-start gap-2 text-xs text-slate-500">
                      <FaArrowRight className="mt-1 shrink-0" />

                      <span className="break-words">
                        {leg.destination}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shrink-0 text-left sm:text-right">
                <div className="text-lg font-black text-slate-900">
                  {money(leg.amount)} €
                </div>

                <div className="mt-1 text-xs text-slate-500">
                  {km(leg.distance_km)} km
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 pl-0 sm:pl-8">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                <FaUsers size={11} />
                {number(leg.passengers)} passager(s)
              </span>

              <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                {rate(leg.rate)} €/km
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* ARRETS DECLARES */}

      {stops.length > 0 && (
        <div className="border-t border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700">
            <FaMapMarkerAlt className="text-indigo-500" />
            Prises en charge et déposes
          </div>

          <div className="space-y-2">
            {stops.map((stop, index) => (
              <StopMovements
                key={`${index}-${stop.address}`}
                stop={stop}
                index={index}
              />
            ))}
          </div>
        </div>
      )}

      {/* TOTAL PARCOURS */}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">
          Total {title.toLowerCase()}
        </span>

        <div className="text-right">
          <div className="text-sm font-bold text-slate-900">
            {money(journey.montant_trajet)} €
          </div>

          <div className="text-xs text-slate-500">
            {km(journey.distance_km)} km
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   COMPOSANT PRINCIPAL
   ========================================================================== */

export default function ExpenseMileageDetails({
  mileageDetail,
  savedDistance,
  savedAmount,
}) {
  const [expanded, setExpanded] = useState(true);

  const detail =
    mileageDetail &&
    typeof mileageDetail === "object"
      ? mileageDetail
      : null;

  const result = detail?.result;

  if (!result || !result.aller) return null;

  const totalKm = number(savedDistance);
  const totalAmount = number(savedAmount);

  const hasReturn = !!result.retour;

  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-indigo-200 bg-indigo-50/40">
      {/* HEADER */}

      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition hover:bg-indigo-50 sm:px-5"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
            <FaRoute />
          </div>

          <div>
            <div className="text-sm font-black text-slate-900">
              Détail des indemnités kilométriques
            </div>

            <div className="mt-1 text-xs text-slate-500">
              Calcul par tronçon selon les passagers transportés
            </div>
          </div>
        </div>

        {expanded ? (
          <FaChevronUp className="shrink-0 text-indigo-600" />
        ) : (
          <FaChevronDown className="shrink-0 text-indigo-600" />
        )}
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-indigo-100 p-3 sm:p-5">
          {/* SYNTHESE */}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Distance totale
              </div>

              <div className="mt-2 text-xl font-black text-slate-900">
                {km(totalKm)} km
              </div>
            </div>

            <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Taux moyen
              </div>

              <div className="mt-2 text-xl font-black text-indigo-700">
                {rate(result.taux_moyen)} €/km
              </div>

              <div className="mt-1 text-xs text-slate-500">
                Variable par tronçon
              </div>
            </div>

            <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200">
              <div className="text-xs font-semibold uppercase text-emerald-700">
                Montant enregistré
              </div>

              <div className="mt-2 text-xl font-black text-emerald-900">
                {money(totalAmount)} €
              </div>
            </div>
          </div>

          {/* ALLER */}

          <JourneyDetails
            title="Trajet aller"
            journey={result.aller}
            declaredJourney={detail.outbound}
            variant="blue"
          />

          {/* RETOUR */}

          {hasReturn && (
            <JourneyDetails
              title="Trajet retour"
              journey={result.retour}
              declaredJourney={detail.inbound}
              variant="violet"
            />
          )}

          {/* INFORMATION CONTROLE */}

          <div className="rounded-xl bg-white p-3 text-xs leading-5 text-slate-600 ring-1 ring-slate-200">
            Les distances, les taux et les montants présentés
            correspondent au calcul enregistré avec la fiche.
            Les montées et descentes de passagers déterminent
            le taux applicable à chaque tronçon.
          </div>
        </div>
      )}
    </div>
  );
}
